import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import { BillConsumerRenderProps, withBillContext } from 'components/bill/BillContext';
import BillDataSegment from 'components/bill/BillDataSegment';
import PrintProofListPopup from 'components/bill/PrintProofListPopup';
import {
  ConsultationsConsumerRenderProps,
  withConsultationsContext
} from 'components/consultations/ConsultationsContext';
import CheckBox from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import ErrorMessage from 'components/final-form/ErrorMessage';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import LoaderComponent from 'components/LoaderComponent';
import { PatientConsumerRenderProps, withPatientContext } from 'components/patient/PatientContext';
import PdfModal from 'components/pdf-modal/PdfModal';
import { TreatmentsConsumerRenderProps, withTreatmentsContext } from 'components/treatments/TreatmentsContext';
import arrayMutators from 'final-form-arrays';
import createDecorator from 'final-form-calculate';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Divider, Grid, Header, Icon, Modal, RadioProps, Table } from 'semantic-ui-react';
import { billTreatment, getBillingDataForTreatment, getBillPdfUrl, reCalculateBillPrice } from 'services/billServices';
import { getAllMandateBanksByMandateId } from 'services/mandateBankServices';
import styled from 'styled-components';
import { BillPaymentType, BillPrintOption, BillStatus } from 'ts-types/api.enums';
import {
  BillCreationRequest,
  BillCreationResponse,
  BillingDataDto,
  BillPriceRequest,
  ConsultationBillingDataDto,
  MandateBankDto
} from 'ts-types/api.types';
import { currencyFormatter, numberFormatter } from 'utils/fomattingUtils';

const ManualBillingModalContentPane = styled(FormModalContentContainer)`

  .manual-billing-form.ui.grid > .row {
    padding-bottom: 0;
  }

  .manual-billing-form.ui.grid > .divider-row.row {
    padding-top: 0.5rem;

    > .column {
      padding-right: 0;
    }
  }

  .billing-options-pane {
    margin-bottom: 2.5rem;
  }

  .consultations-table .checkbox-column {
    display: inline-flex;
    align-items: center;
  }
`;

const ScrollableTableWrapper = styled.div`
  height: 422px;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  border: 1px solid rgba(34, 36, 38, .15);
  box-shadow: none;
  border-radius: .28571429rem;

  & .ui.table {
    border: none;
  }

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
    border-bottom: 1px solid rgba(34, 36, 38, .15)
  }
`;

const BoldedInfo = styled.div`
  font-weight: bold;
`;

const rightSegmentStyle = {
  padding: 0,
  marginLeft: "12px"
};

interface ExtendedConsultationBillingDataDto extends ConsultationBillingDataDto {
  used: boolean
}

interface ExtendedBillCreationRequest extends BillCreationRequest {
  consultations: Array<ExtendedConsultationBillingDataDto>
}

interface Props extends MessageSourceProps,
    AuthConsumerRenderProps,
    BillConsumerRenderProps,
    PatientConsumerRenderProps,
    TreatmentsConsumerRenderProps,
    ConsultationsConsumerRenderProps {
  modalOpen: boolean,
  onCloseModal: () => void,
  treatmentId: number
}

interface State {
  billingData?: BillingDataDto,
  cancelTokenSource: CancelTokenSource,
  mandateBanks: Array<DropdownOption>,
  billPaymentTypes: Array<RadioProps>,
  loadingData: boolean,
  pdfModalOpen: boolean,
  billCreationResponses: Array<BillCreationResponse>,
  billCreated: boolean,
  errorMessage?: string
}

class ManualBillingModal extends Component<Props, State> {

  private currentDate = moment().format("YYYY-MM-DD");

  private initialValues: Partial<ExtendedBillCreationRequest> = {};

  constructor(props: Props) {
    super(props);

    const {treatmentId, currentUser, getMessage} = this.props;

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      billingData: undefined,
      cancelTokenSource: cancelTokenSource,
      mandateBanks: [],
      billPaymentTypes: generateRadioButtonOptionsFromEnum(Object.values(BillPaymentType), "bill", getMessage),
      loadingData: true,
      pdfModalOpen: false,
      billCreationResponses: [],
      billCreated: false
    };

    const requests: Array<Promise<any>> = [
      getBillingDataForTreatment(treatmentId, cancelTokenSource),
      getAllMandateBanksByMandateId(currentUser!.userConfig.mandateId, cancelTokenSource)
    ];

    Promise
        .all(requests)
        .then(responses => {

          const mandateBanks: Array<MandateBankDto> = responses[1];

          const mappedBanks: Array<DropdownOption> = mandateBanks.map(mandateBank => ({
            key: mandateBank.id,
            text: `${mandateBank.description}, ${mandateBank.city}, (${mandateBank.bankAccount})`,
            value: mandateBank.id
          }));

          const billingData = responses[0];
          this.setState({
            billingData: billingData,
            mandateBanks: mappedBanks
          });

          this.initialValues = this.createInitialValues(billingData, mappedBanks, this.props);
        })
        .finally(() => {
          this.setState({
            loadingData: false
          });
        });
  }

  isXmlAvailable = (): boolean => {
    const {billingData} = this.state;
    return billingData !== undefined && billingData.guarantorId !== null && billingData.isTarmedTariff;
  }

  createInitialValues = (
      billingData: BillingDataDto,
      mandateBanks: DropdownOption[],
      props: Props): Partial<ExtendedBillCreationRequest> => {

    const {options} = props;

    const mappedConsultationBillingData = billingData ?
        billingData.consultationBillingData.map(consultation => ({
          ...consultation,
          used: this.currentDate >= consultation.consultationDate
        })) : [];

    return {
      billAmount: billingData && billingData.billAmount,
      billAdvanceAmount: billingData && billingData.billAdvanceAmount,
      billDate: options.workDate,
      bankId: mandateBanks.length ? mandateBanks[0].value : -1,
      billPaymentType: BillPaymentType.BILL,
      consultations: mappedConsultationBillingData,
      isDetailed: true,
      xmlInvoice: this.isXmlAvailable()
    }
  };

  consultations = createDecorator({
    field: 'consultations',
    updates: {
      billAmount: (consultations: Array<ExtendedConsultationBillingDataDto>, allValues?: Partial<BillingDataDto>) => {

        const {cancelTokenSource, billingData} = this.state;

        if (billingData) {

          const excludedIds = consultations
              .filter(consultation =>
                  !consultation.used && this.currentDate < consultation.consultationDate
              )
              .map(excludedConsultation => excludedConsultation.consultationId);

          const billPriceRequest: BillPriceRequest = {
            billDataId: billingData.billDataId,
            treatmentId: billingData.treatmentId,
            excludedConsultationEntries: excludedIds
          };

          return reCalculateBillPrice(billPriceRequest, cancelTokenSource)
              .then(response => response);
        }

        return allValues ? allValues.billAmount : undefined;
      }
    }
  });

  handleSubmit = async (values: Partial<ExtendedBillCreationRequest>) => {

    const error = this.validateForm(values);
    if (error) {
      return {
        error: error
      }
    }

    const {
      currentUser,
      currentPatient,
      treatmentId,
      modifyTreatmentBillStatus,
      modifyConsultationsBillStatus,
      appendBills,
      getMessage
    } = this.props;

    const {billingData, billCreated, cancelTokenSource} = this.state;

    if (billCreated) {
      // guard to prevent creating a bill twice
      return;
    }

    const excludedConsultations = values.consultations ? values.consultations
        .filter(consultation => (
            !consultation.used &&
            this.currentDate < consultation.consultationDate
        )) : [];

    const selectedConsultations = values.consultations
        ? values.consultations.filter(consultation => (
            consultation.used
        ))
        : [];

    if (values.billPrintOption === BillPrintOption.PROOF_LIST && !values.isDetailed) {
      values.billAdvanceAmount = undefined;
    }

    const billCreationRequest: Partial<BillCreationRequest> = {
      ...values,
      excludedConsultations: excludedConsultations,
      selectedConsultations: selectedConsultations,
      billDataId: billingData ? billingData.billDataId : undefined,
      userId: currentUser!.id,
      patientId: currentPatient!.id,
      treatmentId: treatmentId,
      mandateId: currentUser!.userConfig.mandateId
    };

    let errorMessage: any = undefined;
    billTreatment(billCreationRequest, cancelTokenSource)
        .then(response => {
          const {treatmentBillStatus, consultationBillStatus, consultationIds} = response;

          modifyTreatmentBillStatus(treatmentBillStatus as BillStatus);
          appendBills(response.bills);

          if (consultationBillStatus && consultationIds.length > 0) {
            const uniqueConsultationIds = new Set<number>(consultationIds);
            modifyConsultationsBillStatus(uniqueConsultationIds, consultationBillStatus as BillStatus);
          }

          const billCreationResponses = [response];
          this.setState({
            pdfModalOpen: true,
            billCreationResponses,
            billCreated: billCreationRequest.billPrintOption === undefined
          });
        })
        .catch((error: AxiosError) => {
          const {response} = error;

          if (response && response.data) {
            const {violations} = response.data;

            if (violations && violations.length > 0) {
              violations.forEach((violation: any) => {
                if (violation.errorCode == "INVOICE_XML_ERROR") {
                  this.setErrorMessage(violation.details.split("\n"));
                  errorMessage = {
                    errorHeader: getMessage("error.xmlGeneration"),
                    errorContent: violation.details.split("\n")
                  };
                } else {
                  violation.details
                      ? this.setErrorMessage(violation.details)
                      : this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
                }
              });
            }
          }

          if (errorMessage == undefined) {
            errorMessage = getMessage("error.general");
          }
        });
  };

  getBillPdfUrl = (response: BillCreationResponse) => {
    return getBillPdfUrl(response.pdfId, response.pdfName);
  };

  closeManualBillingPdfModal = () => {
    this.setState({
      pdfModalOpen: false,
      billCreationResponses: []
    });

    if (this.state.billCreated) {
      this.props.onCloseModal();
    }
  };

  validateForm = (values: Partial<ExtendedBillCreationRequest>): string | undefined => {

    const {getMessage} = this.props;
    const {billingData} = this.state;

    const {billAmount, billAdvanceAmount, billPaymentType, consultations} = values;

    if (billingData && billingData.consultationBillingData.length == 0) {
      return getMessage("bill.noConsultationsPresent");
    }

    const excludedConsultations = consultations ? consultations.filter(consultation => !consultation.used) : [];

    if (consultations && excludedConsultations.length == consultations.length) {
      return getMessage("bill.everyConsultationExcluded");
    }

    if (!billAmount && !billAdvanceAmount) {
      return getMessage("bill.billAmountNotPresent");
    }

    if (!billAmount && !!billAdvanceAmount && billPaymentType != BillPaymentType.INTERMEDIATE_BILL) {
      return getMessage("bill.onlyIntermediateBillCreation");
    }

    const consultationsWithInvalidDate = consultations
        ? consultations.filter(consultation =>
            consultation.used && this.currentDate < consultation.consultationDate
        )
        : [];

    if (consultations && consultationsWithInvalidDate.length > 0) {
      return getMessage("bill.consultationsWithInvalidDate");
    }
  };

  setErrorMessage = (message?: string) => {
    this.setState({
      errorMessage: message
    })
  }

  render(): React.ReactNode {

    const {getMessage} = this.props;
    const {loadingData} = this.state;

    return loadingData ?
        <LoaderComponent
            message={getMessage("bill.loading")}
            dimmerProps={{active: true, inverted: false, page: true}}
            inverted
        />
        : this.renderModal();
  }

  renderModal = (): React.ReactNode => {

    const {modalOpen, onCloseModal, getMessage} = this.props;
    const {pdfModalOpen, billCreationResponses} = this.state;

    return (
        <Modal
            open={modalOpen}
            onClose={onCloseModal}
            style={{width: "850px"}}
            closeOnDimmerClick={false}
        >
          <Modal.Header>
            {getMessage("manualBilling")}
          </Modal.Header>
          <ManualBillingModalContentPane>
            <FinalForm
                onSubmit={this.handleSubmit}
                subscription={{pristine: true, values: true, submitting: true}}
                initialValues={this.initialValues}
                mutators={{
                  ...arrayMutators
                }}
                decorators={[
                  this.consultations
                ]}
                render={this.renderForm}
            />
          </ManualBillingModalContentPane>
          {
              pdfModalOpen && <PdfModal
                  modalOpen={pdfModalOpen}
                  onCloseModal={this.closeManualBillingPdfModal}
                  pdfResponses={billCreationResponses}
                  getPdfUrl={this.getBillPdfUrl}
              />
          }
        </Modal>
    )
  };

  renderForm = ({submitting, handleSubmit, form, values}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCloseModal} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <Field
                  name="error"
                  component={ErrorMessage}
              />
              {errorMessage &&
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage(undefined)}>
                    {errorMessage}
                  </StyledErrorMessage>
              }
              <Grid className="manual-billing-form">
                <Grid.Row>
                  <Grid.Column width={7}>
                    {this.renderBillInfoSegment(values)}
                  </Grid.Column>
                  <Grid.Column width={9}>
                    {this.renderConsultationsSegment()}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row className="divider-row">
                  <Grid.Column width={16}>
                    <Divider style={{margin: "0", marginTop: "3px"}}/>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    {this.renderBillDataSegment()}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    {this.renderOptionsSegment()}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </FormContent>

            <FormButtonsRow>
              <PrintProofListPopup
                  values={values}
                  form={form}
                  handleSubmit={this.handleSubmit}
                  disabled={submitting}
              />
              <Button
                  type="submit"
                  primary
                  compact
                  disabled={submitting}
                  onClick={() => form.change("billPaymentType", BillPaymentType.BILL)}
              >
                {getMessage("bill")}
              </Button>
              <Button
                  type="submit"
                  primary
                  compact
                  disabled={submitting}
                  onClick={() => form.change("billPaymentType", BillPaymentType.INTERMEDIATE_BILL)}
              >
                {getMessage("bill.intermediate")}
              </Button>
              <Button
                  type="submit"
                  primary
                  compact
                  disabled={submitting}
                  onClick={() => form.change("billPaymentType", BillPaymentType.CASH)}
              >
                {getMessage("bill.cashPayment")}
              </Button>
              <Button compact type="button" floated="right" onClick={onCloseModal}
                      disabled={submitting}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    )
  };

  renderBillInfoSegment = (values: Partial<ExtendedBillCreationRequest>): React.ReactNode => {

    const {billingData} = this.state;
    const {getMessage} = this.props;

    const treatmentDate = billingData ? moment(billingData.treatmentDate).format("DD.MM.YYYY") : "";

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("bill.information")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={7} verticalAlign="middle">
              <BoldedInfo>
                {treatmentDate}
              </BoldedInfo>
            </Grid.Column>
            <Grid.Column width={9} verticalAlign="middle">
              <BoldedInfo>
                {billingData && billingData.tariffInfo}
              </BoldedInfo>
            </Grid.Column>
          </Grid.Row>
          {!!values.billAmount && +values.billAmount == +values.billAmount ?
              <Grid.Row>
                <Grid.Column width={7} verticalAlign="middle">
                  <BoldedInfo>
                    {getMessage("bill")}:
                  </BoldedInfo>
                </Grid.Column>
                <Grid.Column width={5} verticalAlign="middle">
                  <BoldedInfo>
                    {currencyFormatter.format(values.billAmount)}
                  </BoldedInfo>
                </Grid.Column>
                {
                    !!values.installmentQuantity && !!values.installmentDays &&
                    <Grid.Column width={4} verticalAlign="middle">
                      <BoldedInfo>
                        {`${values.installmentQuantity}/${values.installmentDays}`}
                      </BoldedInfo>
                    </Grid.Column>
                }
              </Grid.Row> :
              <Grid.Row/>
          }
          {!!values.billAdvanceAmount && +values.billAdvanceAmount == +values.billAdvanceAmount ?
              <Grid.Row>
                <Grid.Column width={7} verticalAlign="middle">
                  <BoldedInfo>
                    {getMessage("bill.advancePayment")}:
                  </BoldedInfo>
                </Grid.Column>
                <Grid.Column width={9} verticalAlign="middle">
                  <BoldedInfo>
                    {currencyFormatter.format(values.billAdvanceAmount)}
                  </BoldedInfo>
                </Grid.Column>
              </Grid.Row> :
              <Grid.Row/>
          }
          <Divider style={{margin: "1rem 0rem 1rem 1rem"}}/>
          <Grid.Row>
            <Grid.Column verticalAlign="middle">
              <Header as="h4">
                {getMessage("bill.patient")}:
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <BoldedInfo>
                {billingData && `${billingData.patientName}, ${billingData.patientFirstName}`}
              </BoldedInfo>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <BoldedInfo>
                {billingData && `${billingData.patientCity}, ${billingData.patientBirthDate}`}
              </BoldedInfo>
            </Grid.Column>
          </Grid.Row>
          <Divider style={{margin: "1rem 0rem 1rem 1rem"}}/>
          <Grid.Row>
            <Grid.Column verticalAlign="middle">
              <Header as="h4">
                {getMessage("bill.guarantor")}:
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <BoldedInfo>
                {billingData && billingData.guarantorId && billingData.guarantorDescription}
              </BoldedInfo>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <BoldedInfo>
                {billingData && billingData.guarantorId && `${billingData.guarantorZip}, ${billingData.guarantorCity}`}
              </BoldedInfo>
            </Grid.Column>
          </Grid.Row>
          <Divider style={{margin: "1rem 0rem 1rem 1rem"}}/>
        </PraxFormGrid>
    )
  };

  renderBillDataSegment = (): React.ReactNode => {

    const {billingData} = this.state;

    return <BillDataSegment billingData={billingData}/>;
  };

  renderOptionsSegment = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {mandateBanks} = this.state;

    const xmlAvailable = this.isXmlAvailable();

    return (
        <PraxFormGrid className="billing-options-pane">
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("options")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              <label htmlFor="billDate">{getMessage("date")}:</label>
              <Field
                  name="billDate"
                  component={DatePicker}
              />
            </Grid.Column>

            <Grid.Column width={8} verticalAlign="middle">
              <label htmlFor="bankId">{getMessage("bill.bank")}:</label>
              <Field
                  name="bankId"
                  component={Select}
                  options={mandateBanks}
                  fluid
              />
            </Grid.Column>

            {xmlAvailable &&
                <Grid.Column width={3}>
                  <div>&nbsp;</div>
                  <Field
                      name="xmlInvoice"
                      component={CheckBox}
                      label={`${getMessage("treatmentReason.xmlkey")}`}
                  />
                </Grid.Column>
            }
          </Grid.Row>
        </PraxFormGrid>
    )
  };

  renderConsultationsSegment = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <FieldArray name="consultations">
          {({fields}) =>
              <ScrollableTableWrapper style={rightSegmentStyle}>
                <Table className="consultations-table" celled size="small">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{getMessage("used")}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage("date")}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage("time")}</Table.HeaderCell>
                      <Table.HeaderCell>{getMessage("amount")}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {fields.value.map((consultation: ExtendedConsultationBillingDataDto, index: number) => (
                        <Table.Row key={consultation.consultationId}>
                          <Table.Cell>
                            <div className="checkbox-column">
                              <Field
                                  name={`consultations[${index}].used`}
                                  component={CheckBox}
                                  disabled={this.currentDate < consultation.consultationDate}
                              />
                              {
                                  this.currentDate < consultation.consultationDate &&
                                  <Icon
                                      name="warning sign"
                                      color="red"
                                      title={getMessage("bill.invalidConsultationDate.title")}
                                  />
                              }
                            </div>
                          </Table.Cell>
                          <Table.Cell>{moment(consultation.consultationDate).format("DD.MM.YYYY")}</Table.Cell>
                          <Table.Cell>{moment(consultation.consultationTime, "HH:mm:ss").format("HH:mm")}</Table.Cell>
                          <Table.Cell
                              textAlign="right">{numberFormatter.format(consultation.consultationAmount)}</Table.Cell>
                        </Table.Row>
                    ))
                    }
                  </Table.Body>
                </Table>
              </ScrollableTableWrapper>
          }
        </FieldArray>
    )
  };
}

const ManualBillingModalWrapper =
    withMessages(
        withAuthContext(
            withTreatmentsContext(
                withBillContext(
                    withPatientContext(
                        withConsultationsContext(
                            ManualBillingModal))))));

export default ManualBillingModalWrapper;
